import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  Typography,
  IconButton,
  Chip,
  Stack,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Paper,
} from '@mui/material';
import {
  MyLocation,
  Search,
  Edit,
  Check,
  Add,
  Home,
  Work,
  Place,
  GpsFixed,
  BookmarkBorder,
  Bookmark,
  Delete,
} from '@mui/icons-material';
import { Loader } from '@googlemaps/js-api-loader';
import apiService from '../services/api';

// Google Maps configuration
const GOOGLE_MAPS_CONFIG = {
  apiKey: process.env.REACT_APP_GOOGLE_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyD_mock_key_get_real_one',
  version: 'weekly',
  libraries: ['places']
};

const LocationPicker = ({ 
  initialLatitude, 
  initialLongitude, 
  initialAddress,
  onLocationChange,
  label = "Location",
  required = false,
  showAddressBook = true,
  allowSaveToAddressBook = true,
  placeholder = "Search for an address...",
  mode = "full", // "full", "simple", "profile"
  countryRestriction = "za", // Restrict to South Africa
  useDefaultAddress = false // If true, shows default address first with "Change Location" option
}) => {
  const [address, setAddress] = useState(initialAddress || '');
  const [latitude, setLatitude] = useState(initialLatitude || '');
  const [longitude, setLongitude] = useState(initialLongitude || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [geocodedAddress, setGeocodedAddress] = useState(initialAddress || '');
  
  // Google Places Autocomplete
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  
  // Address book state
  const [addressBook, setAddressBook] = useState([]);
  const [showAddressBookDialog, setShowAddressBookDialog] = useState(false);
  const [saveToAddressBook, setSaveToAddressBook] = useState(false);
  const [addressLabel, setAddressLabel] = useState('');
  const [addressBookLoading, setAddressBookLoading] = useState(false);
  
  // Default address mode state
  const [showingDefaultAddress, setShowingDefaultAddress] = useState(false);
  const [defaultAddress, setDefaultAddress] = useState(null);

  // Load Google Maps API
  useEffect(() => {
    const initGoogleMaps = async () => {
      try {

        
        // Check if API key is valid (not the mock key)
        if (GOOGLE_MAPS_CONFIG.apiKey.includes('mock') || GOOGLE_MAPS_CONFIG.apiKey.length < 20) {
          throw new Error('Invalid Google Maps API key');
        }
        
        const loader = new Loader(GOOGLE_MAPS_CONFIG);
        await loader.load();
        
        // Verify the Places library is loaded
        if (window.google && window.google.maps && window.google.maps.places) {

          setGoogleMapsLoaded(true);
        } else {
          throw new Error('Places library not loaded');
        }
      } catch (error) {
        console.error('Error loading Google Maps:', error);
        
        // Provide specific error messages
        if (error.message.includes('API key')) {
          console.warn('Google Maps API key issue. Using fallback geocoding.');
        } else if (error.message.includes('blocked') || error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
          console.warn('Google Maps blocked by ad blocker. Using fallback geocoding.');
        } else {
          console.warn('Google Maps failed to load. Using fallback geocoding.');
        }
        
        setGoogleMapsLoaded(false);
      }
    };

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      initGoogleMaps();
    } else {

      setGoogleMapsLoaded(true);
    }
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (googleMapsLoaded && inputRef.current && !autocompleteRef.current) {
      try {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          inputRef.current,
          {
            componentRestrictions: { country: countryRestriction },
            fields: ['place_id', 'geometry', 'name', 'formatted_address', 'address_components'],
            types: ['address'] // Focus on addresses rather than businesses
          }
        );

        autocompleteRef.current.addListener('place_changed', handlePlaceSelect);

      } catch (error) {
        console.error('Error initializing autocomplete:', error);
        setError('Google Places blocked by ad blocker. Address search will use manual entry mode.');
        setGoogleMapsLoaded(false); // Disable Google features
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleMapsLoaded, countryRestriction]);

  // Load address book on component mount
  useEffect(() => {
    if (showAddressBook) {
      loadAddressBook();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddressBook]);

  // Update geocodedAddress when initialAddress changes
  useEffect(() => {
    if (initialAddress && initialAddress !== geocodedAddress) {
      setGeocodedAddress(initialAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAddress]);

  // Notify parent when location changes
  useEffect(() => {
    if (onLocationChange) {
      const locationData = {
        latitude: latitude ? parseFloat(parseFloat(latitude).toFixed(6)) : null,
        longitude: longitude ? parseFloat(parseFloat(longitude).toFixed(6)) : null,
        address: address || geocodedAddress,
        formattedAddress: geocodedAddress,
        place: selectedPlace
      };

      // Add parsed address components if we have a Google Place
      if (selectedPlace && selectedPlace.address_components) {
        locationData.addressComponents = parseAddressComponents(selectedPlace);
      }

      onLocationChange(locationData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude, address, geocodedAddress, selectedPlace]);

  const parseAddressComponents = (place) => {
    const components = place.address_components || [];
    const addressData = {
      streetNumber: '',
      streetName: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      fullAddress: place.formatted_address || ''
    };

    components.forEach(component => {
      const types = component.types;
      
      if (types.includes('street_number')) {
        addressData.streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        addressData.streetName = component.long_name;
      }
      if (types.includes('locality') || types.includes('sublocality')) {
        addressData.city = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        addressData.state = component.long_name;
      }
      if (types.includes('postal_code')) {
        addressData.postalCode = component.long_name;
      }
      if (types.includes('country')) {
        addressData.country = component.long_name;
      }
    });

    // Combine street number and name
    addressData.streetAddress = [addressData.streetNumber, addressData.streetName]
      .filter(Boolean)
      .join(' ');

    return addressData;
  };

  const loadAddressBook = async () => {
    try {
      setAddressBookLoading(true);
      const response = await apiService.getAddressBook();
      
      // Handle different response formats (array, paginated, etc.)
      let addresses = [];
      if (Array.isArray(response)) {
        addresses = response;
      } else if (response && Array.isArray(response.results)) {
        // Django REST Framework paginated response
        addresses = response.results;
      } else if (response && typeof response === 'object') {
        // Single object response
        addresses = [response];
      }
      
      setAddressBook(addresses);

      
      // Handle default address mode
      if (useDefaultAddress && addresses.length > 0) {
        // Find default address or use first address
        const defaultAddr = addresses.find(addr => addr.is_default) || addresses[0];
        setDefaultAddress(defaultAddr);
        
        // If no location is already set, show the default address
        if (!latitude && !longitude && !address) {
          setShowingDefaultAddress(true);
          setAddress(defaultAddr.formatted_address);
          setLatitude(defaultAddr.latitude.toString());
          setLongitude(defaultAddr.longitude.toString());
          setGeocodedAddress(defaultAddr.formatted_address);
        }
      }
    } catch (err) {
      console.error('Failed to load address book:', err);
      setAddressBook([]); // Ensure it's always an array
    } finally {
      setAddressBookLoading(false);
    }
  };

  const handlePlaceSelect = useCallback(() => {
    const place = autocompleteRef.current.getPlace();
    
    if (!place.geometry) {
      setError('No location found for this address');
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    setSelectedPlace(place);
    setLatitude(lat.toFixed(6));
    setLongitude(lng.toFixed(6));
    setAddress(place.formatted_address);
    setGeocodedAddress(place.formatted_address);
    setSuccess('Location selected from Google Places!');
    setTimeout(() => setSuccess(''), 3000);
  }, []);

  const handleAddressInputChange = (event) => {
    setAddress(event.target.value);
    // Clear previous selection when typing
    if (selectedPlace) {
      setSelectedPlace(null);
      setLatitude('');
      setLongitude('');
      setGeocodedAddress('');
    }
  };

  const handleAddressBookSelect = (savedAddress) => {
    setAddress(savedAddress.formatted_address);
    setLatitude(savedAddress.latitude.toString());
    setLongitude(savedAddress.longitude.toString());
    setGeocodedAddress(savedAddress.formatted_address);
    setSuccess(`Using saved address: ${savedAddress.label}`);
    setTimeout(() => setSuccess(''), 3000);
    setShowAddressBookDialog(false);
  };

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      return;
    }

    setLoading(true);
    setError('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
        
        // Use Google Geocoding Service if available, fallback to our API
        try {
          if (googleMapsLoaded && window.google) {
            const geocoder = new window.google.maps.Geocoder();
            const response = await new Promise((resolve, reject) => {
              geocoder.geocode(
                { location: { lat, lng } },
                (results, status) => {
                  if (status === 'OK' && results[0]) {
                    resolve(results[0]);
                  } else {
                    reject(new Error('Geocoding failed'));
                  }
                }
              );
            });
            
            setGeocodedAddress(response.formatted_address);
            setAddress(response.formatted_address);
            setSuccess('Current location detected with Google Maps!');
          } else {
            // Fallback to our API
            const result = await apiService.reverseGeocode(lat, lng);
            setGeocodedAddress(result.address);
            setAddress(result.address);
            setSuccess('Current location detected successfully!');
          }
        } catch (err) {
          const coords = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
          setGeocodedAddress(coords);
          setAddress(coords);
          setSuccess('Location coordinates detected');
        }
        
        setLoading(false);
        setTimeout(() => setSuccess(''), 3000);
      },
      (error) => {
        setLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setError('Location access denied. Please enable location access.');
            break;
          case error.POSITION_UNAVAILABLE:
            setError('Location information is unavailable.');
            break;
          case error.TIMEOUT:
            setError('Location request timed out.');
            break;
          default:
            setError('An unknown error occurred while getting location.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const geocodeManualAddress = async () => {
    if (!address.trim()) {
      setError('Please enter an address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Try Google Geocoding first if available
      if (googleMapsLoaded && window.google) {
        const geocoder = new window.google.maps.Geocoder();
        const response = await new Promise((resolve, reject) => {
          geocoder.geocode(
            { 
              address: address.trim(),
              componentRestrictions: { country: countryRestriction }
            },
            (results, status) => {
              if (status === 'OK' && results[0]) {
                resolve(results[0]);
              } else {
                reject(new Error('Address not found'));
              }
            }
          );
        });
        
        const lat = response.geometry.location.lat();
        const lng = response.geometry.location.lng();
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
        setGeocodedAddress(response.formatted_address);
        setSuccess('Address found with Google Maps!');
      } else {
        // Fallback to our API
        const result = await apiService.geocodeAddress(address.trim());
        setLatitude(result.lat.toFixed(6));
        setLongitude(result.lng.toFixed(6));
        setGeocodedAddress(result.formatted_address);
        setSuccess('Address found successfully!');
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Could not find address. Please try a different address or use manual coordinates.');
    } finally {
      setLoading(false);
    }
  };



  const handleSaveToAddressBook = async () => {
    if (!latitude || !longitude || !addressLabel.trim()) {
      setError('Please provide both location and a label for the address');
      return;
    }

    try {
      setLoading(true);
      await apiService.createAddress({
        label: addressLabel.trim(),
        formatted_address: geocodedAddress || address,
        latitude: parseFloat(parseFloat(latitude).toFixed(6)),
        longitude: parseFloat(parseFloat(longitude).toFixed(6)),
        is_default: false
      });
      
      setSuccess(`Address saved as "${addressLabel}"`);
      setSaveToAddressBook(false);
      setAddressLabel('');
      await loadAddressBook(); // Refresh address book
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save address to address book');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await apiService.deleteAddress(addressId);
      setSuccess('Address deleted');
      await loadAddressBook(); // Refresh address book
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete address');
    }
  };

  const clearLocation = () => {
    setLatitude('');
    setLongitude('');
    setGeocodedAddress('');
    setAddress('');
    setSelectedPlace(null);
    setShowingDefaultAddress(false);
    
    // Clear Google Places Autocomplete
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const changeLocation = () => {
    setShowingDefaultAddress(false);
  };

  const hasLocation = latitude && longitude;

  // Get address book grouped by type
  const safeAddressBook = Array.isArray(addressBook) ? addressBook : [];

  // Check if current location already exists in address book
  const isLocationAlreadySaved = () => {
    if (!hasLocation || !safeAddressBook.length) return false;
    
    const currentLat = parseFloat(latitude).toFixed(6);
    const currentLng = parseFloat(longitude).toFixed(6);
    
    return safeAddressBook.some(addr => 
      parseFloat(addr.latitude).toFixed(6) === currentLat && 
      parseFloat(addr.longitude).toFixed(6) === currentLng
    );
  };

  const locationAlreadySaved = isLocationAlreadySaved();
  const groupedAddresses = {
    home: safeAddressBook.filter(addr => addr.label.toLowerCase().includes('home')),
    work: safeAddressBook.filter(addr => addr.label.toLowerCase().includes('work')),
    other: safeAddressBook.filter(addr => 
      !addr.label.toLowerCase().includes('home') && 
      !addr.label.toLowerCase().includes('work')
    )
  };

  const getAddressIcon = (label) => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes('home')) return <Home />;
    if (lowerLabel.includes('work')) return <Work />;
    return <Place />;
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        <GpsFixed sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </Typography>

      {/* Current Location Display */}
      {hasLocation && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          icon={<GpsFixed />}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" component="span">
              <strong>Location set:</strong> {geocodedAddress || address || 'Address not available'}
            </Typography>
            {selectedPlace && (
              <Chip 
                size="small" 
                label="Google Places" 
                color="success" 
              />
            )}
          </Box>
        </Alert>
      )}

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Google Maps API Status */}
      {!googleMapsLoaded && !showingDefaultAddress && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Google Places unavailable</strong> - This may be due to an ad blocker or network issue. 
            You can still enter addresses manually and use GPS location.
          </Typography>
        </Alert>
      )}

      {/* Default Address Display */}
      {useDefaultAddress && showingDefaultAddress && defaultAddress && (
        <Paper 
          sx={{ 
            p: 3, 
            mb: 2, 
            bgcolor: 'success.50', 
            border: '1px solid', 
            borderColor: 'success.200' 
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {getAddressIcon(defaultAddress.label)}
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {defaultAddress.label}
                  {defaultAddress.is_default && (
                    <Chip 
                      label="Default" 
                      size="small" 
                      color="primary" 
                      sx={{ ml: 1 }} 
                    />
                  )}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {defaultAddress.formatted_address}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={changeLocation}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Change Location
            </Button>
          </Box>
        </Paper>
      )}

      {/* Location Input Methods */}
      {!hasLocation && !showingDefaultAddress && (
        <Stack spacing={3}>
          {/* Address Book Quick Access */}
          {showAddressBook && safeAddressBook.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <BookmarkBorder sx={{ mr: 0.5, fontSize: 16 }} />
                Saved Addresses
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {safeAddressBook.slice(0, 3).map((addr) => (
                  <Chip
                    key={addr.id}
                    icon={getAddressIcon(addr.label)}
                    label={addr.label}
                    variant="outlined"
                    onClick={() => handleAddressBookSelect(addr)}
                    sx={{ mb: 1 }}
                  />
                ))}
                {safeAddressBook.length > 3 && (
                  <Chip
                    icon={<Add />}
                    label={`+${safeAddressBook.length - 3} more`}
                    variant="outlined"
                    onClick={() => setShowAddressBookDialog(true)}
                    sx={{ mb: 1 }}
                  />
                )}
              </Stack>
            </Box>
          )}

          {/* Current Location Button */}
          <Box>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <MyLocation />}
              onClick={getCurrentLocation}
              disabled={loading}
              fullWidth
              sx={{ textTransform: 'none' }}
            >
              {loading ? 'Getting Location...' : 'Use My Current Location'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Click to automatically detect your location using GPS
            </Typography>
          </Box>

          {/* Google Places Address Search */}
          <Box>
            <TextField
              fullWidth
              inputRef={inputRef}
              label="Search for an address"
              placeholder={placeholder}
              value={address}
              onChange={handleAddressInputChange}
              InputProps={{
                startAdornment: (
                  <Search sx={{ mr: 1, color: 'text.secondary' }} />
                ),
                endAdornment: (
                  <>
                    {!googleMapsLoaded && <CircularProgress size={20} sx={{ mr: 1 }} />}
                    {address && !selectedPlace && (
                      <IconButton
                        size="small"
                        onClick={geocodeManualAddress}
                        disabled={loading}
                        title="Search this address"
                      >
                        <GpsFixed />
                      </IconButton>
                    )}
                  </>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !selectedPlace) {
                  geocodeManualAddress();
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {googleMapsLoaded 
                ? "Start typing for Google Places suggestions, or press Enter to search"
                : "Type an address and press Enter to search (Google Places disabled)"
              }
            </Typography>
          </Box>

        </Stack>
      )}

      {/* Save to Address Book Option */}
      {hasLocation && allowSaveToAddressBook && !showingDefaultAddress && !locationAlreadySaved && (
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={saveToAddressBook}
                onChange={(e) => setSaveToAddressBook(e.target.checked)}
              />
            }
            label="Save this address to my address book"
          />
          {saveToAddressBook && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="Address label"
                value={addressLabel}
                onChange={(e) => setAddressLabel(e.target.value)}
                placeholder="e.g., Home, Work, Gym"
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveToAddressBook}
                disabled={loading || !addressLabel.trim()}
                startIcon={<Bookmark />}
                sx={{ textTransform: 'none' }}
              >
                Save
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Edit Existing Location */}
      {hasLocation && !showingDefaultAddress && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={clearLocation}
            sx={{ textTransform: 'none' }}
          >
            Change Location
          </Button>
          {showAddressBook && (
            <Button
              variant="outlined"
              startIcon={<Bookmark />}
              onClick={() => setShowAddressBookDialog(true)}
              sx={{ textTransform: 'none' }}
            >
              Address Book
            </Button>
          )}
        </Box>
      )}

      {/* Address Book Dialog */}
      <Dialog 
        open={showAddressBookDialog} 
        onClose={() => setShowAddressBookDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Bookmark sx={{ mr: 1 }} />
            My Address Book
          </Box>
        </DialogTitle>
        <DialogContent>
          {addressBookLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : safeAddressBook.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No saved addresses yet
            </Typography>
          ) : (
            <List>
              {/* Home addresses */}
              {groupedAddresses.home.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, pt: 1 }}>
                    Home
                  </Typography>
                  {groupedAddresses.home.map((addr) => (
                    <ListItem key={addr.id} divider>
                      <ListItemIcon>
                        <Home />
                      </ListItemIcon>
                      <ListItemText
                        primary={addr.label}
                        secondary={addr.formatted_address}
                      />
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleAddressBookSelect(addr)}
                          title="Use this address"
                        >
                          <Check />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteAddress(addr.id)}
                          title="Delete address"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </>
              )}

              {/* Work addresses */}
              {groupedAddresses.work.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, pt: 1 }}>
                    Work
                  </Typography>
                  {groupedAddresses.work.map((addr) => (
                    <ListItem key={addr.id} divider>
                      <ListItemIcon>
                        <Work />
                      </ListItemIcon>
                      <ListItemText
                        primary={addr.label}
                        secondary={addr.formatted_address}
                      />
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleAddressBookSelect(addr)}
                          title="Use this address"
                        >
                          <Check />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteAddress(addr.id)}
                          title="Delete address"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </>
              )}

              {/* Other addresses */}
              {groupedAddresses.other.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, pt: 1 }}>
                    Other
                  </Typography>
                  {groupedAddresses.other.map((addr) => (
                    <ListItem key={addr.id} divider>
                      <ListItemIcon>
                        <Place />
                      </ListItemIcon>
                      <ListItemText
                        primary={addr.label}
                        secondary={addr.formatted_address}
                      />
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleAddressBookSelect(addr)}
                          title="Use this address"
                        >
                          <Check />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteAddress(addr.id)}
                          title="Delete address"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddressBookDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationPicker; 